import { Fragment, useEffect } from "react"
import { Transition } from "@headlessui/react"
import classNames from "classnames"
import { Icon, IconLibraryKeys } from "../Icon"
import { useToastDispatch } from "../../context/ToastContextProvider"

export type ToastMessageStatus = "success" | "attention" | "critical" | "x"

interface Props {
  id: string
  status: ToastMessageStatus
  text: string
  header: string
}

type StatusMap<T> = { [key in ToastMessageStatus]: T }

const iconMap: StatusMap<IconLibraryKeys> = {
  success: "CheckCircleOutline",
  attention: "Exclamation",
  critical: "Exclamation",
  x: "X",
}

/**
 * ToastMessage will delete itself after 3 seconds if it is not dismissed by the user.
 */
export function ToastMessage({ id, status, text, header }: Props) {
  const dispatch = useToastDispatch()

  useEffect(() => {
    let timer = setTimeout(() => dispatch({ type: "DESTROY_TOAST", id }), 3000)
    return () => clearTimeout(timer)
  }, [dispatch, id])

  return (
    <>
      <div
        aria-live="assertive"
        className="pointer-events-none relative items-end px-4 py-6 md:p-6 w-[460px] sm:items-start sm:p-6 w-96"
      >
        <div className="flex w-full flex-col items-center space-y-4 sm:items-end">
          <Transition
            show={true}
            as={Fragment}
            enter="transform ease-out duration-300 transition"
            enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
            enterTo="translate-y-0 opacity-100 sm:translate-x-0"
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div
              className={`${
                status === "success"
                  ? "bg-green-800"
                  : status === "critical"
                  ? "bg-red-800"
                  : "bg-white"
              } pointer-events-auto w-full overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5`}
            >
              <div className="p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <Icon
                      variant={iconMap[status]}
                      className={classNames(
                        "h-6 w-6",
                        { "text-white": status === "success" },
                        { "text-white": status === "critical" },
                        { "text-orange-400": status === "attention" },
                      )}
                      aria-hidden="true"
                    />
                  </div>
                  <div className="ml-3 w-0 flex-1 pt-0.5">
                    <p
                      className={`text-sm font-medium ${
                        status === "success" || status === "critical"
                          ? "text-white"
                          : "text-gray-900"
                      }`}
                    >
                      {header}
                    </p>
                    <p
                      className={`mt-1 text-sm ${
                        status === "success" || status === "critical"
                          ? "text-white"
                          : "text-gray-500"
                      }`}
                    >
                      {text}
                    </p>
                  </div>
                  <div className="ml-4 flex flex-shrink-0">
                    <button
                      type="button"
                      className={`inline-flex rounded-md bg-transparent ${
                        status === "success" ? "text-white" : "text-gray-400"
                      } hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`}
                      onClick={() => dispatch({ type: "DESTROY_TOAST", id })}
                    >
                      <span className="sr-only">Close</span>
                      <Icon
                        variant={iconMap["x"]}
                        className="h-5 w-5"
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </>
  )
}
